/**
 * @fileoverview CheckboxGroup widget — multi-select with min/max + PASS/FAIL.
 * @module inspection-renderer/fields
 *
 * @component CheckboxGroupField
 */
import type { FieldOption } from '@firesquirrel/core-domain';
import { FieldWrapper } from './FieldWrapper';
import type { BaseWidgetProps } from './widgetTypes';
import { getPassFailStatus } from '../violations/passFailState';

interface OptionWithPassFail extends FieldOption {
  passFail?: 'pass' | 'fail' | 'neutral';
}

export function CheckboxGroupField(props: BaseWidgetProps<string[] | null | undefined>) {
  const { field, value, isNA, resolved, onChange, onToggleNA, errorMessage, requiredUnmet } = props;
  const extras = (field.extras ?? {}) as { options?: OptionWithPassFail[]; minSelected?: number; maxSelected?: number };
  const options = (resolved?.options as OptionWithPassFail[] | undefined) ?? extras.options ?? [];
  const selectedValues = value ?? [];
  const belowMin = extras.minSelected != null
    && !isNA
    && selectedValues.length < extras.minSelected
    && (selectedValues.length > 0 || field.required || resolved?.required);
  const computedError = errorMessage
    ?? (belowMin ? `Select at least ${extras.minSelected}` : undefined);

  function toggle(v: string) {
    const set = new Set(selectedValues);
    if (set.has(v)) set.delete(v);
    else if (extras.maxSelected == null || set.size < extras.maxSelected) set.add(v);
    onChange(Array.from(set));
  }

  // Pass/fail is owned by `extras.hasPassFail` + `extras.failValues` — see
  // `passFailState.ts`. Violation code bindings are metadata only.
  const passFailStatus = getPassFailStatus(field, selectedValues, isNA);

  return (
    <FieldWrapper
      fieldId={field.id}
      widgetType="checkboxGroup"
      label={field.label}
      helpText={field.helpText}
      required={field.required}
      resolved={resolved}
      isNA={isNA}
      naEnabled={field.naEnabled}
      onToggleNA={onToggleNA}
      passFailStatus={passFailStatus}
      errorMessage={computedError}
      requiredUnmet={requiredUnmet} requiredImmediate={props.requiredImmediate}
    >
      <fieldset role="group" aria-required={!!(field.required || resolved?.required)}>
        <legend className="sr-only">{field.label}</legend>
        <div className="flex flex-col gap-2">
          {options.map((o) => (
            <label key={o.value} className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={selectedValues.includes(o.value)}
                onChange={() => toggle(o.value)}
                disabled={!!resolved?.disabled}
                data-passfail={o.passFail ?? 'neutral'}
              />
              <span>{o.label}</span>
            </label>
          ))}
        </div>
      </fieldset>
    </FieldWrapper>
  );
}
